import axios, { type AxiosResponse } from 'axios'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { clearAuthToken, getAuthToken, setAuthToken } from '@/shared/lib/auth-token'

/**
 * django-allauth headless "app" client.
 *
 * Auth *actions* (login, logout, session probe, password reset, login-by-code,
 * MFA) go through allauth's headless endpoints, which speak the allauth
 * envelope `{ status, data, meta }` - distinct from our business envelope
 * `{ status, message, result }`. This client therefore has its own axios
 * instance (no business-envelope interceptor) and injects the session token.
 *
 * allauth uses HTTP 401 for legitimate "not authenticated" *responses* (the
 * session probe when logged out; the logout reply; a login that still needs a
 * second factor). Those are not errors here, so all non-5xx resolve and callers
 * read `meta.is_authenticated` / `data.flows` rather than catching.
 */

const HEADLESS_BASE = '/api/_allauth/app/v1'

export interface AllauthFlow {
  id: string
  is_pending?: boolean
  [key: string]: unknown
}

export interface AllauthResponse<T = Record<string, unknown>> {
  status: number
  data?: T & { flows?: AllauthFlow[] }
  meta?: {
    is_authenticated?: boolean
    session_token?: string
    access_token?: string
  }
  errors?: Array<{ message: string; code?: string; param?: string }>
}

const authClient = axios.create({
  baseURL: buildApiUrl(HEADLESS_BASE),
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
  // Resolve every non-5xx so callers inspect the allauth envelope instead of
  // catching the expected 400/401/409/410 auth responses.
  validateStatus: (status) => status >= 200 && status < 500,
})

/**
 * True for the login endpoint, which must NEVER carry a session token.
 *
 * allauth's app client derives `request.user` purely from the `X-Session-Token`
 * header (it discards the session cookie), and its LoginView answers 409
 * "already authenticated" whenever that header resolves a live session. Login
 * has to establish a *fresh* session, so the token must not ride along - see
 * the interceptor below.
 */
export function isLoginEndpoint(url: string | undefined): boolean {
  if (!url) return false
  const path = url.split('?')[0].replace(/\/+$/, '')
  // Handle both absolute (`/api/.../auth/login`) and relative (`auth/login`) forms.
  return path === 'auth/login' || path.endsWith('/auth/login')
}

authClient.interceptors.request.use((config) => {
  const token = getAuthToken()
  // Attach the session token to every request EXCEPT login. Sending a live
  // token on login triggers allauth's 409, and because axios request
  // interceptors run asynchronously, a concurrent session probe can restore the
  // token in the microtask gap right after `headlessLogin()` clears it - so
  // clearing alone is racy. Skipping login here removes the 409 deterministically.
  if (token && !isLoginEndpoint(config.url)) config.headers['X-Session-Token'] = token
  return config
})

/**
 * Persist the session token allauth returns. allauth issues a token for any
 * authenticated *or in-progress* (e.g. pending-MFA) session, so store it
 * whenever present and let callers decide from `meta.is_authenticated`.
 */
function captureSessionToken(res: AxiosResponse): AllauthResponse {
  const body = (res.data ?? {}) as AllauthResponse
  const token = body.meta?.session_token
  if (token) setAuthToken(token)
  return body
}

export function isAuthenticated(body: AllauthResponse): boolean {
  return body.meta?.is_authenticated === true
}

/**
 * Whether a login response was rejected because a session is already active
 * (allauth's 409 "already authenticated"). Distinct from bad credentials - the
 * caller should surface a session-conflict message, not "invalid password".
 */
export function isSessionConflict(body: AllauthResponse): boolean {
  return body.status === 409
}

/**
 * Expire the Django session COOKIE (`__session`) server-side.
 *
 * A stale login session lives on two transports: the `X-Session-Token` (which
 * we control from JS) and the HttpOnly `__session` cookie (which we cannot read
 * or clear from JS). Some edges/proxies map that cookie back into the auth
 * token, so a lingering cookie keeps re-establishing the session that makes
 * allauth answer 409 on the next login. The legacy `POST /api/auth/logout/`
 * endpoint is cookie-authenticated (`CsrfExemptSessionAuthentication`,
 * `AllowAny`) and runs Django `logout()`, which flushes the session and emits a
 * `Set-Cookie` that expires `__session` in the browser.
 *
 * Deliberately a bare axios call (not `authClient`): it must NOT carry the
 * `X-Session-Token` header, because the backend's session-token bridge strips
 * the response `Set-Cookie` on any token-bearing request - which would swallow
 * the very cookie deletion we need. Best-effort: any failure just leaves the
 * retry to fail visibly rather than throwing.
 */
async function clearCookieSession(): Promise<void> {
  try {
    await axios.post(buildApiUrl('/api/auth/logout/'), null, {
      // Same-origin in prod (cookie flows regardless); withCredentials lets the
      // cookie flow in a cross-origin dev setup too.
      withCredentials: true,
      validateStatus: (status) => status >= 200 && status < 500,
    })
  } catch {
    // best-effort
  }
}

export async function headlessLogin(
  email: string,
  password: string,
  rememberMe = false,
): Promise<AllauthResponse> {
  // Remember the current token so the recovery logout below can identify the
  // lingering server-side session; `headlessLogout()` reads `getAuthToken()`.
  const oldToken = getAuthToken()
  // Drop any stored token so we start a fresh session. The login request itself
  // never carries the token (see `isLoginEndpoint` + the interceptor), which is
  // what prevents allauth's 409 "already authenticated"; clearing here just
  // keeps local state tidy while the new token is issued.
  clearAuthToken()
  // "Keep me logged in": opt this session into the extended server-side lifetime
  // (SESSION_REMEMBER_ME_AGE). The backend's user_logged_in receiver reads this
  // header and calls request.session.set_expiry(); omitting it keeps the default
  // ~1-month window. Sent on the retry too so a healed 409 keeps the choice.
  const loginConfig = rememberMe ? { headers: { 'X-Remember-Me': '1' } } : undefined
  let res = await authClient.post('/auth/login', { email, password }, loginConfig)
  // If a 409 still comes back, a stale session is still winning - typically the
  // HttpOnly `__session` cookie (which JS can't clear and which an edge may map
  // back into the auth token), or a token re-attached in the interceptor's
  // microtask gap. Silently tear the session down on BOTH transports and retry
  // once so the user notices nothing (no error, no manual cookie-clearing):
  //   - the token session: restore the token so allauth's app logout can target
  //     it (`headlessLogout()` clears the token again in its `finally`), and
  //   - the cookie session: `clearCookieSession()` expires `__session`.
  // The retry then starts with no token and no cookie -> allauth sees an
  // anonymous request -> a clean 200. Match the HTTP status as well as the
  // allauth envelope so a non-JSON proxy 409 still triggers recovery.
  if (res.status === 409 || res.data?.status === 409) {
    if (oldToken) setAuthToken(oldToken)
    await headlessLogout().catch(() => {})
    await clearCookieSession()
    res = await authClient.post('/auth/login', { email, password }, loginConfig)
  }
  return captureSessionToken(res)
}

export async function fetchHeadlessSession(): Promise<AllauthResponse> {
  const res = await authClient.get('/auth/session')
  const body = captureSessionToken(res)
  if (body.status === 410) clearAuthToken()
  return body
}

export async function headlessLogout(): Promise<void> {
  // allauth replies 401 (now unauthenticated) on success; either way the local
  // token must go. The server deletes the session, which revokes the token.
  try {
    await authClient.delete('/auth/session')
  } finally {
    clearAuthToken()
  }
}

/**
 * Whether a login response is paused at the two-factor stage.
 *
 * After valid credentials, allauth replies 401 with `meta.is_authenticated:
 * false` and a `mfa_authenticate` flow marked `is_pending: true` (a bare
 * `mfa_authenticate` flow without that marker just advertises the capability).
 * The session token is already issued and stored, so the caller prompts for a
 * code and completes the stage via `authenticate2fa`.
 */
export function isMfaPending(body: AllauthResponse): boolean {
  return Boolean(
    body.data?.flows?.some(
      (flow) => flow.id === 'mfa_authenticate' && flow.is_pending === true,
    ),
  )
}

/** Complete a pending two-factor login stage with a TOTP / recovery code. */
export async function authenticate2fa(code: string): Promise<AllauthResponse> {
  const res = await authClient.post('/auth/2fa/authenticate', { code })
  return captureSessionToken(res)
}

/**
 * Whether a response asks the (already authenticated) user to re-enter their
 * password before a sensitive operation. allauth gates managing authenticators
 * behind recent authentication: a 401 carrying a `reauthenticate` flow while
 * still `meta.is_authenticated` means "confirm your password, then retry".
 */
export function isReauthRequired(body: AllauthResponse): boolean {
  return Boolean(
    body.meta?.is_authenticated &&
      body.data?.flows?.some((flow) => flow.id === 'reauthenticate'),
  )
}

/** Confirm the current user's password to refresh the reauthentication window. */
export async function reauthenticate(password: string): Promise<AllauthResponse> {
  const res = await authClient.post('/auth/reauthenticate', { password })
  return captureSessionToken(res)
}

// ---------------------------------------------------------------------------
// TOTP authenticator management (Settings -> Security)
// ---------------------------------------------------------------------------

export interface TotpAuthenticatorData {
  type: string
  created_at: number
  last_used_at: number | null
}

/**
 * Fetch the current user's TOTP authenticator state.
 *
 * allauth returns 404 with `meta.secret` + `meta.totp_url` when the user is not
 * yet enrolled (everything needed to set one up), and 200 with the
 * authenticator data once enrolled. The 404 is an expected response here, not
 * an error - read `body.status` to branch.
 */
export async function getTotpAuthenticator(): Promise<
  AllauthResponse<TotpAuthenticatorData>
> {
  const res = await authClient.get('/account/authenticators/totp')
  return (res.data ?? {}) as AllauthResponse<TotpAuthenticatorData>
}

/** Activate TOTP by confirming a code generated from the setup secret. */
export async function activateTotp(
  code: string,
): Promise<AllauthResponse<TotpAuthenticatorData>> {
  const res = await authClient.post('/account/authenticators/totp', { code })
  return (res.data ?? {}) as AllauthResponse<TotpAuthenticatorData>
}

/** Remove the current user's TOTP authenticator. */
export async function deleteTotp(): Promise<AllauthResponse> {
  const res = await authClient.delete('/account/authenticators/totp')
  return (res.data ?? {}) as AllauthResponse
}

// ---------------------------------------------------------------------------
// Password reset (headless)
// ---------------------------------------------------------------------------

/**
 * Request a password-reset email. Always resolves (allauth does not reveal
 * whether the address exists); the email deep-links back to this SPA.
 */
export async function requestPasswordReset(
  email: string,
  tenantSlug?: string,
): Promise<AllauthResponse> {
  // Forward the tenant (from the ?tenant deep link) so the backend can apply
  // tenant branding to the reset email; omitted means generic iWarranty branding.
  const res = await authClient.post(
    '/auth/password/request',
    { email },
    tenantSlug ? { headers: { 'X-Tenant': tenantSlug } } : undefined,
  )
  return (res.data ?? {}) as AllauthResponse
}

/** Set a new password using the opaque `key` from the reset email link. */
export async function resetPassword(
  key: string,
  password: string,
): Promise<AllauthResponse> {
  const res = await authClient.post('/auth/password/reset', { key, password })
  return captureSessionToken(res)
}

export { authClient as headlessAuthClient }
